import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getRunEventStatus } from '../api/api';
import { QK } from '../lib/query-keys';
import { useCurrentBotId } from './use-current-bot-id';

// Matches aistudio PollingBanner's default `pollingInterval` prop
// (client/components/PollingBanner.vue) — both sides watch the same
// event-status record, so there's no reason to poll it faster here.
const EVENT_STATUS_POLL_MS = 60_000;

/**
 * Keeps the run list's status/recommendation cells from going stale while a
 * run is active, without hammering the list endpoint.
 *
 * Polls `event-status` — a single cheap record — on an interval, exactly what
 * aistudio's own PollingBanner does to drive its banner. `listCalibrationRuns`
 * is refetched exactly once, on an actual status transition, not on the same
 * interval: an unconditional `refetchInterval` on the list query was hitting
 * GET /observation-run continuously for as long as any run stayed active.
 *
 * @param hasActiveRun Gate from the run list's own data (see
 *   `isInProgressStatus`) — polling stops entirely once nothing is running,
 *   matching PollingBanner's stopPolling once its tracked run goes terminal.
 */
export function useObservationRunStatusSync(hasActiveRun: boolean): void {
  const queryClient = useQueryClient();
  const lastStatusRef = useRef<string | undefined>(undefined);
  const botId = useCurrentBotId();

  const { data } = useQuery({
    // botId in the key so a bot switch starts tracking that bot's event
    // status instead of continuing to poll/report on the previous bot's.
    queryKey: QK.calibration.eventStatus(botId),
    queryFn: ({ signal }) => getRunEventStatus(signal),
    enabled: hasActiveRun,
    refetchInterval: hasActiveRun ? EVENT_STATUS_POLL_MS : false,
  });

  useEffect(() => {
    // Reset so re-arming later (a new run starts after this one settles, or
    // the bot changes) is seen as a transition again, not suppressed by a
    // stale ref value.
    if (!hasActiveRun) {
      lastStatusRef.current = undefined;
      return;
    }
    if (!data?.status || data.status === lastStatusRef.current) return;
    lastStatusRef.current = data.status;
    queryClient.invalidateQueries({ queryKey: QK.calibration.runs.all });
  }, [data, hasActiveRun, queryClient]);
}
